import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useRole } from "@/utils/useRole";

/*
 * كل صفحة تُحمَّل عند فتحها لا مع أول تحميل.
 *
 * كانت الحزمة الأولى 1.8 ميجا لأنها تضم مكتبات الرسوم والإكسل والوورد
 * التي لا تخص إلا صفحات بعينها، فكانت شاشة الدخول تنتظرها كلها —
 * قياساً 65 ثانية على شبكة المدرسة.
 */
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ReportingCenterPage = lazy(() => import("@/pages/ReportingCenterPage"));
const UploadPage = lazy(() => import("@/pages/UploadPage"));
const LeaderboardsPage = lazy(() => import("@/pages/LeaderboardsPage"));
const AssessmentsPage = lazy(() => import("@/pages/AssessmentsPage"));
const UserActivityPage = lazy(() => import("@/pages/UserActivityPage"));
const TeachersPerformancePage = lazy(() =>
  import("@/pages/TeachersPerformancePage")
);
const StudentsPage = lazy(() => import("@/pages/StudentsPage"));
const ComparisonsPage = lazy(() => import("@/pages/ComparisonsPage"));
const TermSettingsPage = lazy(() => import("@/pages/TermSettingsPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const ElearningSessionsPage = lazy(() =>
  import("@/pages/ElearningSessionsPage")
);
const MeetingsPage = lazy(() => import("@/pages/MeetingsPage"));
const PdPlanPage = lazy(() => import("@/pages/PdPlanPage"));
const TermReportPage = lazy(() => import("@/pages/TermReportPage"));
const StudentCardsPage = lazy(() => import("@/pages/StudentCardsPage"));
const PracticesPage = lazy(() => import("@/pages/PracticesPage"));
const GuideTrackerPage = lazy(() => import("@/pages/GuideTrackerPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));

const Spinner = () => (
  <div className="flex min-h-screen items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

/**
 * جدول المسارات حسب الدور.
 *
 * المعلم لا تُركَّب له صفحات الأدمن أصلاً، فلا تُطلق استعلامات يرفضها
 * الخادم — وكان رفضها يُظهر شاشة بيضاء عند دخوله. أي مسار آخر يعيده
 * إلى حصص التعليم الإلكتروني.
 */
const RoleRoutes = () => {
  const { isAdmin, loading } = useRole();

  if (loading) return <Spinner />;

  if (!isAdmin) {
    return (
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route
            path="/elearning-sessions"
            element={<ElearningSessionsPage />}
          />
          <Route
            path="*"
            element={<Navigate to="/elearning-sessions" replace />}
          />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/reporting-center" element={<ReportingCenterPage />} />
        <Route path="/upload" element={<UploadPage />} />
        <Route path="/leaderboards" element={<LeaderboardsPage />} />
        <Route path="/assessments" element={<AssessmentsPage />} />
        <Route path="/user-activity" element={<UserActivityPage />} />
        <Route
          path="/teachers-performance"
          element={<TeachersPerformancePage />}
        />
        <Route path="/students" element={<StudentsPage />} />
        <Route path="/comparisons" element={<ComparisonsPage />} />
        <Route path="/term-settings" element={<TermSettingsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/elearning-sessions" element={<ElearningSessionsPage />} />
        <Route path="/visits-meetings" element={<MeetingsPage />} />
        <Route path="/skills-development" element={<PdPlanPage />} />
        <Route path="/planning" element={<TermReportPage />} />
        <Route path="/student-cards" element={<StudentCardsPage />} />
        <Route path="/practices" element={<PracticesPage />} />
        <Route path="/guide-tracker" element={<GuideTrackerPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default RoleRoutes;
